import { resolvePlanChangeDirection } from './planChangeIntent';

type BillingCycle = 'monthly' | 'yearly';
type SelectablePlanCode =
    | 'SOLO_199'
    | 'SOLO_399'
    | 'BASIC'
    | 'SOLO_999'
    | 'SOLO_1299'
    | 'TEAM_1499'
    | 'TEAM_2999'
    | 'PRO'
    | 'TEAM_9999'
    | 'TEAM_49999';

export interface ScheduledPlanChange {
    changeType?: string | null;
    effectiveMode?: string | null;
    effectiveAt?: string | null;
    fromPlanCode?: string | null;
    fromPlanName?: string | null;
    fromBillingCycle?: string | null;
    toPlanCode?: string | null;
    toPlanName?: string | null;
    toBillingCycle?: string | null;
}

interface ResolveScheduledPlanBubbleStateInput {
    scheduledChange?: ScheduledPlanChange | null;
    currentPlanCode?: SelectablePlanCode | string | null;
    currentBillingCycle?: BillingCycle | string | null;
    planCode: SelectablePlanCode;
    billingCycle: BillingCycle;
}

const normalizeCode = (value?: string | null) => (value || '').toUpperCase();
const normalizeChangeType = (value?: string | null) => (value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
const normalizeCycle = (value?: string | null): BillingCycle | null => {
    if (!value) return null;
    return value === 'yearly' ? 'yearly' : value === 'monthly' ? 'monthly' : null;
};

export const formatScheduledEffectiveDate = (
    value?: string | null,
    locale: string = 'en-US',
): string | null => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(date);
};

const formatPlanLabel = ({
    planName,
    planCode,
    billingCycle,
    currentPlanCode,
    currentPlanName,
    currentBillingCycle,
}: {
    planName?: string | null;
    planCode?: string | null;
    billingCycle?: string | null;
    currentPlanCode?: string | null;
    currentPlanName?: string | null;
    currentBillingCycle?: string | null;
}): string => {
    const baseName = planName || planCode || 'selected plan';
    const normalizedTargetCycle = normalizeCycle(billingCycle);
    const normalizedCurrentCycle = normalizeCycle(currentBillingCycle);
    const shouldAppendCycle = Boolean(
        normalizedTargetCycle
        && normalizedTargetCycle !== normalizedCurrentCycle,
    );

    if (!shouldAppendCycle || !normalizedTargetCycle) {
        return baseName;
    }

    const cycleLabel = normalizedTargetCycle === 'yearly' ? 'Yearly' : 'Monthly';
    return `${baseName} ${cycleLabel}`;
};

interface ResolveScheduledPlanButtonStateInput {
    scheduledChange?: ScheduledPlanChange | null;
    currentPlanCode?: SelectablePlanCode | string | null;
    currentBillingCycle?: BillingCycle | string | null;
    planCode: SelectablePlanCode;
    billingCycle: BillingCycle;
}

const getScheduledTargetLabel = (scheduledChange: ScheduledPlanChange): string => formatPlanLabel({
    planName: scheduledChange.toPlanName,
    planCode: scheduledChange.toPlanCode,
    billingCycle: scheduledChange.toBillingCycle,
    currentPlanCode: scheduledChange.fromPlanCode,
    currentPlanName: scheduledChange.fromPlanName,
    currentBillingCycle: scheduledChange.fromBillingCycle,
});

const isScheduledChangeStillRelevant = ({
    scheduledChange,
    currentPlanCode,
    currentBillingCycle,
}: {
    scheduledChange?: ScheduledPlanChange | null;
    currentPlanCode?: SelectablePlanCode | string | null;
    currentBillingCycle?: BillingCycle | string | null;
}): boolean => {
    if (!scheduledChange) {
        return false;
    }

    const normalizedCurrentPlanCode = normalizeCode(currentPlanCode);
    const normalizedCurrentBillingCycle = normalizeCycle(currentBillingCycle);
    const normalizedFromPlanCode = normalizeCode(scheduledChange.fromPlanCode);
    const normalizedFromBillingCycle = normalizeCycle(scheduledChange.fromBillingCycle);

    if (normalizedCurrentPlanCode && normalizedFromPlanCode && normalizedCurrentPlanCode !== normalizedFromPlanCode) {
        return false;
    }

    if (normalizedCurrentBillingCycle && normalizedFromBillingCycle && normalizedCurrentBillingCycle !== normalizedFromBillingCycle) {
        return false;
    }

    return true;
};

export interface ScheduledPlanBubbleState {
    kind: 'current-plan' | 'scheduled-target';
    planLabel: string;
    effectiveAt: string;
    billingCycle: BillingCycle | null;
}

export const resolveScheduledPlanBubbleState = ({
    scheduledChange,
    currentPlanCode,
    currentBillingCycle,
    planCode,
    billingCycle,
}: ResolveScheduledPlanBubbleStateInput): ScheduledPlanBubbleState | null => {
    if (!scheduledChange || scheduledChange.effectiveMode !== 'next_cycle') {
        return null;
    }

    if (!isScheduledChangeStillRelevant({
        scheduledChange,
        currentPlanCode,
        currentBillingCycle,
    })) {
        return null;
    }

    if (!formatScheduledEffectiveDate(scheduledChange.effectiveAt)) {
        return null;
    }

    const normalizedCurrentPlanCode = normalizeCode(currentPlanCode);
    const normalizedCurrentBillingCycle = normalizeCycle(currentBillingCycle);
    const toPlanCode = normalizeCode(scheduledChange.toPlanCode);
    const toBillingCycle = normalizeCycle(scheduledChange.toBillingCycle);
    const currentLabel = scheduledChange.fromPlanName || scheduledChange.fromPlanCode || 'current plan';

    if (planCode === normalizedCurrentPlanCode && billingCycle === normalizedCurrentBillingCycle) {
        return {
            kind: 'current-plan',
            planLabel: currentLabel,
            effectiveAt: scheduledChange.effectiveAt as string,
            billingCycle: null,
        };
    }

    if (planCode === toPlanCode && billingCycle === toBillingCycle) {
        return {
            kind: 'scheduled-target',
            planLabel: scheduledChange.toPlanName || scheduledChange.toPlanCode || 'selected plan',
            effectiveAt: scheduledChange.effectiveAt as string,
            billingCycle: toBillingCycle !== normalizedCurrentBillingCycle ? toBillingCycle : null,
        };
    }

    return null;
};

export const resolveScheduledPlanButtonState = ({
    scheduledChange,
    currentPlanCode,
    currentBillingCycle,
    planCode,
    billingCycle,
}: ResolveScheduledPlanButtonStateInput): { buttonText: string; isDisabled: boolean } | null => {
    if (!scheduledChange || scheduledChange.effectiveMode !== 'next_cycle') {
        return null;
    }

    if (!isScheduledChangeStillRelevant({
        scheduledChange,
        currentPlanCode,
        currentBillingCycle,
    })) {
        return null;
    }

    const toPlanCode = normalizeCode(scheduledChange.toPlanCode);
    const toBillingCycle = normalizeCycle(scheduledChange.toBillingCycle);
    const normalizedCurrentPlanCode = normalizeCode(currentPlanCode);
    const normalizedCurrentBillingCycle = normalizeCycle(currentBillingCycle);

    if (planCode === normalizedCurrentPlanCode && billingCycle === normalizedCurrentBillingCycle) {
        return {
            buttonText: 'Current Plan',
            isDisabled: true,
        };
    }

    if (planCode !== toPlanCode || billingCycle !== toBillingCycle) {
        return null;
    }

    const fromPlanCode = normalizeCode(scheduledChange.fromPlanCode);
    const fromBillingCycle = normalizeCycle(scheduledChange.fromBillingCycle);

    if (toPlanCode === fromPlanCode && toBillingCycle && fromBillingCycle && toBillingCycle !== fromBillingCycle) {
        return {
            buttonText: `Switch to ${toBillingCycle === 'yearly' ? 'Yearly' : 'Monthly'}`,
            isDisabled: true,
        };
    }

    const changeType = normalizeChangeType(scheduledChange.changeType);
    const planChangeDirection = resolvePlanChangeDirection(fromPlanCode, toPlanCode);
    if (changeType.includes('downgrade') || planChangeDirection === 'downgrade') {
        return {
            buttonText: 'Downgrade scheduled',
            isDisabled: true,
        };
    }

    return {
        buttonText: `Switch to ${getScheduledTargetLabel(scheduledChange)}`,
        isDisabled: true,
    };
};
